// src/pages/ClientInvoices.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    Box,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Card,
    CardContent,
    Grid,
    useTheme,
    useMediaQuery,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    ReceiptLong as ReceiptIcon,
    ArrowDownward as DownloadIcon,
    Visibility as ViewIcon,
    Payment as PaymentIcon,
    CalendarToday as CalendarIcon,
    Warning as WarningIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import FiberLoader from '../components/common/FiberLoader';

const parseLocalDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return dateVal;
  const dateStr = String(dateVal);
  const cleanStr = dateStr.split('T')[0].split(' ')[0];
  const parts = cleanStr.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateVal);
};


const ClientInvoices = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
    const [openPromiseDialog, setOpenPromiseDialog] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [promiseDate, setPromiseDate] = useState(addDays(new Date(), 7));
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [paymentBanner, setPaymentBanner] = useState({ open: false, message: '', severity: 'success' });
    const [pdfModal, setPdfModal] = useState({ open: false, invoice: null, format: 'ticket' });

    const handleOpenPdfModal = (invoice, defaultFormat = 'ticket') => {
        setPdfModal({ open: true, invoice, format: defaultFormat });
    };

    useEffect(() => {
        fetchInvoices();
        fetchPaymentMethods();

        // Verificar parámetros de retorno de pago online
        const queryParams = new URLSearchParams(window.location.search);
        const paymentParam = queryParams.get('payment');
        if (paymentParam === 'success') {
            setPaymentBanner({
                open: true,
                message: '¡Su pago ha sido aprobado automáticamente! Su servicio de internet se encuentra activo.',
                severity: 'success'
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (paymentParam === 'failure') {
            setPaymentBanner({
                open: true,
                message: 'El pago en línea a través de la pasarela no pudo ser procesado.',
                severity: 'error'
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (paymentParam === 'pending') {
            setPaymentBanner({
                open: true,
                message: 'Su pago se encuentra en proceso de aprobación por Mercado Pago. El servicio se reactivará tan pronto se valide.',
                severity: 'warning'
            });
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Configurar una actualización periódica cada 30 segundos
        const interval = setInterval(() => {
            fetchInvoices();
        }, 30000);

        // Limpiar el intervalo cuando el componente se desmonte
        return () => clearInterval(interval);
    }, []);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/invoices?_t=${Date.now()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener facturas');
            }

            const data = await response.json();

            // Asegurarnos de que los estados se muestren correctamente
            const processedData = data.map(invoice => {
                // Si el status es 'paid' de la base de datos o si el monto pagado es igual o mayor al total
                if (invoice.status === 'paid' || parseFloat(invoice.paid_amount || 0) >= parseFloat(invoice.amount)) {
                    return {
                        ...invoice,
                        status: 'paid'
                    };
                }
                return invoice;
            });

            setInvoices(processedData);
        } catch (error) {
            console.error('Error fetching invoices:', error);
            setError(error.message || 'Error al cargar las facturas');
        } finally {
            setLoading(false);
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/payments/methods', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setPaymentMethods(data);
            }
        } catch (error) {
            console.error('Error fetching payment methods:', error);
        }
    };

    const handleOpenPaymentDialog = (invoice) => {
        setSelectedInvoice(invoice);
        setOpenPaymentDialog(true);
    };

    const handleClosePaymentDialog = () => {
        setOpenPaymentDialog(false);
        setSelectedInvoice(null);
    };

    const loadCulqiScript = () => {
        return new Promise((resolve) => {
            if (window.Culqi) {
                resolve(true);
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://checkout.culqi.com/js/v4';
            script.async = true;
            script.onload = () => {
                setTimeout(() => resolve(!!window.Culqi), 100);
            };
            script.onerror = () => resolve(false);
            document.head.appendChild(script);
        });
    };

    const handleSelectPaymentMethod = async (method) => {
        if (method.is_online) {
            try {
                const token = localStorage.getItem('clientToken');
                const response = await fetch('/api/client/payments/online/preference', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        invoice_id: selectedInvoice.id,
                        gateway_type: method.gateway_type
                    })
                });

                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.message || 'Error al iniciar pago online');
                }

                const data = await response.json();

                if (method.gateway_type === 'culqi') {
                    handleClosePaymentDialog();
                    setLoading(true);
                    await loadCulqiScript();

                    if (window.Culqi) {
                        window.Culqi.publicKey = data.publicKey;
                        window.Culqi.settings({
                            title: data.title || 'YinoWISP Internet',
                            currency: 'PEN',
                            amount: data.amount
                        });
                        window.Culqi.options({
                            lang: 'auto',
                            modal: true,
                            installments: false,
                            paymentMethods: {
                                tarjeta: true,
                                yape: true,
                                bancaMovil: true,
                                cip: true,
                                cuotealo: true
                            }
                        });

                        window.culqi = async () => {
                            if (window.Culqi.token) {
                                const tokenObj = window.Culqi.token;
                                console.log('[Culqi Token Creado]:', tokenObj.id);
                                window.Culqi.close();

                                try {
                                    const chargeRes = await fetch('/api/client/payments/online/culqi/charge', {
                                        method: 'POST',
                                        headers: {
                                            'Authorization': `Bearer ${token}`,
                                            'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({
                                            token: tokenObj.id,
                                            invoice_id: data.invoiceId
                                        })
                                    });

                                    const chargeData = await chargeRes.json();
                                    if (chargeRes.ok && chargeData.success) {
                                        setPaymentBanner({
                                            open: true,
                                            message: '¡Su pago con Culqi fue procesado exitosamente! El recibo ha sido cancelado y su servicio está activo.',
                                            severity: 'success'
                                        });
                                        fetchInvoices();
                                    } else {
                                        alert(chargeData.message || 'Error al procesar el pago con la tarjeta.');
                                    }
                                } catch (cErr) {
                                    alert('Error al conectar con el servidor.');
                                } finally {
                                    setLoading(false);
                                }
                            } else if (window.Culqi.error) {
                                console.error('[Culqi Error]:', window.Culqi.error);
                                alert(window.Culqi.error.user_message || 'Error al validar datos con Culqi.');
                                setLoading(false);
                            }
                        };

                        setLoading(false);
                        window.Culqi.open();
                    } else {
                        setLoading(false);
                        alert('No se pudo cargar la pasarela Culqi.');
                    }
                } else if (data.checkoutUrl) {
                    window.location.href = data.checkoutUrl;
                } else {
                    throw new Error('No se recibió la URL de pago');
                }
            } catch (err) {
                console.error('Error generating checkout:', err);
                setLoading(false);
                alert(err.message || 'Error al iniciar la pasarela de pago');
            }
        } else {
            handleClosePaymentDialog();
            navigate('/client/payments', { state: { invoiceId: selectedInvoice.id } });
        }
    };

    const handleOpenPromiseDialog = (invoice) => {
        setSelectedInvoice(invoice);
        setPromiseDate(addDays(new Date(), 7));
        setOpenPromiseDialog(true);
    };

    const handleClosePromiseDialog = () => {
        setOpenPromiseDialog(false);
        setSelectedInvoice(null);
    };

    const handlePromiseSubmit = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/invoices/${selectedInvoice.id}/promise`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ promiseDate: format(promiseDate, 'yyyy-MM-dd') })
            });

            if (!response.ok) {
                throw new Error('Error al registrar promesa de pago');
            }

            // Cerrar diálogo y recargar datos
            handleClosePromiseDialog();
            await fetchInvoices(); // Recargar para actualizar estados
            alert('Promesa de pago registrada exitosamente');
        } catch (error) {
            console.error('Error submitting payment promise:', error);
            alert(error.message || 'Error al registrar promesa de pago');
        }
    };

    const handleDownloadInvoice = (invoiceId) => {
        // Obtener el token
        const token = localStorage.getItem('clientToken');

        // Construir la URL
        const downloadUrl = `/api/client/invoices/${invoiceId}/download`;

        // Crear un elemento a temporal para la descarga
        const link = document.createElement('a');
        link.href = downloadUrl;

        // Añadir el token como header de autorización
        if (token) {
            // Para APIs RESTful, usualmente se pasa el token como parámetro de consulta cuando se trata de descargas
            link.href = `${downloadUrl}?token=${token}`;
        }

        // Establecer la descarga como nueva pestaña con nombre de archivo
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        // Hacer clic en el enlace
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Función mejorada para mostrar el estado correcto
    const getStatusChip = (status) => {
        switch (status) {
            case 'paid':
                return <Chip label="Pagada" color="success" size="small" />;
            case 'partially_paid':
                return (
                    <Chip 
                        label="Pago Parcial" 
                        size="small" 
                        sx={{ 
                            backgroundColor: 'rgba(249, 115, 22, 0.1)', 
                            color: '#ea580c', 
                            border: '1px solid rgba(249, 115, 22, 0.2)',
                            fontWeight: 600
                        }} 
                    />
                );
            case 'pending':
                return <Chip label="Pendiente" color="primary" size="small" />;
            case 'overdue':
                return <Chip label="Vencida" color="error" size="small" />;
            case 'promised':
                return <Chip label="Prometida" color="warning" size="small" icon={<CalendarIcon />} />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    // Agregar función para verificar si una factura está pagada
    const isInvoicePaid = (invoice) => {
        return invoice.status === 'paid' ||
            parseFloat(invoice.paid_amount || 0) >= parseFloat(invoice.amount);
    };

    if (loading && invoices.length === 0) {
        return (
            <FiberLoader text="Obteniendo comprobantes de fibra óptica..." subtext="Cargando facturas y recibos del cliente..." fullPage />
        );
    }

    if (error) {
        return (
            <Alert severity="error" className="m-4">
                {error}
            </Alert>
        );
    }

    return (
        <Container maxWidth="lg">
            {paymentBanner.open && (
                <Alert 
                    severity={paymentBanner.severity} 
                    onClose={() => setPaymentBanner(prev => ({ ...prev, open: false }))} 
                    sx={{ mb: 3, borderRadius: '12px', fontWeight: 600 }}
                >
                    {paymentBanner.message}
                </Alert>
            )}
            <Box className="mb-6">
                <Typography variant="h4" component="h1" gutterBottom>
                    <ReceiptIcon className="mr-2 align-top" />
                    Mis Recibos
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Historial de recibos y pagos
                </Typography>
            </Box>

            {invoices.length === 0 ? (
                <Paper className="p-4 text-center" sx={{ borderRadius: 2 }}>
                    <Typography variant="h6">No tienes recibos registrados</Typography>
                </Paper>
            ) : isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {invoices.map((invoice) => (
                        <Card key={invoice.id} elevation={1} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box>
                                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                            {invoice.sunat_status === 'ACEPTADO' && invoice.sunat_number ? (
                                                <Chip
                                                    label={invoice.sunat_number.startsWith('F') ? 'Factura SUNAT' : 'Boleta SUNAT'}
                                                    color="info"
                                                    size="small"
                                                    sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                                />
                                            ) : (
                                                <Chip
                                                    label="Recibo Interno"
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                            {invoice.sunat_status === 'ACEPTADO' && invoice.sunat_number ? invoice.sunat_number : `#${invoice.invoice_number}`}
                                        </Typography>
                                    </Box>
                                    {getStatusChip(invoice.status)}
                                </Box>

                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.2 }}>
                                            Fecha Emisión
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {format(parseLocalDate(invoice.created_at), 'dd/MM/yyyy')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.2 }}>
                                            Vencimiento
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: invoice.status === 'overdue' ? 'error.main' : 'text.primary' }}>
                                            {format(parseLocalDate(invoice.payment_due_date || invoice.paymentDueDate || invoice.due_date), 'dd/MM/yyyy')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.2 }}>
                                            Total
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.85rem' }}>
                                            S/ {parseFloat(invoice.amount).toFixed(2)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.2 }}>
                                            Abonado
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', fontSize: '0.85rem' }}>
                                            S/ {parseFloat(invoice.paid_amount || 0).toFixed(2)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.2 }}>
                                            Pendiente
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'error.main', fontSize: '0.85rem' }}>
                                            S/ {(parseFloat(invoice.amount) - parseFloat(invoice.paid_amount || 0)).toFixed(2)}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleOpenPdfModal(invoice, 'ticket')}
                                        startIcon={<ViewIcon sx={{ fontSize: '1rem !important' }} />}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                    >
                                        Ver PDF
                                    </Button>

                                    {/* Solo mostrar botón de pago si no está pagada */}
                                    {!isInvoicePaid(invoice) && invoice.status !== 'paid' && (
                                        invoice.has_pending_payment ? (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                sx={{ color: '#e0a800', borderColor: '#e0a800', borderRadius: 2 }}
                                                startIcon={<WarningIcon sx={{ fontSize: '1rem !important' }} />}
                                                title="Pago reportado (Pendiente de verificación)"
                                            >
                                                Pendiente
                                            </Button>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => handleOpenPaymentDialog(invoice)}
                                                color="success"
                                                startIcon={<PaymentIcon sx={{ fontSize: '1rem !important' }} />}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                Pagar
                                            </Button>
                                        )
                                    )}

                                    {/* Solo mostrar botón de promesa si está vencida, no pagada y no tiene promesa previa */}
                                    {invoice.status === 'overdue' && !invoice.payment_promise_date && !isInvoicePaid(invoice) && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleOpenPromiseDialog(invoice)}
                                            color="warning"
                                            startIcon={<CalendarIcon sx={{ fontSize: '1rem !important' }} />}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            Promesa
                                        </Button>
                                    )}

                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleOpenPdfModal(invoice, 'ticket');
                                        }}
                                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Comprobante #</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Fecha de Emisión</TableCell>
                                <TableCell>Vencimiento</TableCell>
                                <TableCell>Monto</TableCell>
                                <TableCell>Pagado</TableCell>
                                <TableCell>Saldo Pendiente</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.map((invoice) => (
                                <TableRow key={invoice.id}>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                        {invoice.sunat_status === 'ACEPTADO' && invoice.sunat_number ? invoice.sunat_number : `#${invoice.invoice_number}`}
                                    </TableCell>
                                    <TableCell>
                                        {invoice.sunat_status === 'ACEPTADO' && invoice.sunat_number ? (
                                            <Chip
                                                label={invoice.sunat_number.startsWith('F') ? 'Factura SUNAT' : 'Boleta SUNAT'}
                                                color="info"
                                                size="small"
                                                sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                                            />
                                        ) : (
                                            <Chip
                                                label="Recibo Interno"
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.72rem' }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>{format(parseLocalDate(invoice.created_at), 'dd/MM/yyyy')}</TableCell>
                                    <TableCell>{format(parseLocalDate(invoice.payment_due_date || invoice.paymentDueDate || invoice.due_date), 'dd/MM/yyyy')}</TableCell>
                                    <TableCell>S/ {parseFloat(invoice.amount).toFixed(2)}</TableCell>
                                    <TableCell>S/ {parseFloat(invoice.paid_amount || 0).toFixed(2)}</TableCell>
                                    <TableCell sx={{ color: parseFloat(invoice.amount) - parseFloat(invoice.paid_amount || 0) > 0 ? 'error.main' : 'text.primary', fontWeight: parseFloat(invoice.amount) - parseFloat(invoice.paid_amount || 0) > 0 ? 'bold' : 'normal' }}>
                                        S/ {(parseFloat(invoice.amount) - parseFloat(invoice.paid_amount || 0)).toFixed(2)}
                                    </TableCell>
                                    <TableCell>{getStatusChip(invoice.status)}</TableCell>
                                    <TableCell align="right">
                                        <Box className="flex space-x-1" justifyContent="flex-end">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenPdfModal(invoice, 'ticket')}
                                                color="primary"
                                                title="Ver Comprobante PDF"
                                            >
                                                <ViewIcon fontSize="small" />
                                            </IconButton>

                                            {/* Solo mostrar botón de pago si no está pagada */}
                                            {!isInvoicePaid(invoice) && invoice.status !== 'paid' && (
                                                invoice.has_pending_payment ? (
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        sx={{ color: '#e0a800' }}
                                                        title="Pago reportado (Pendiente de verificación)"
                                                    >
                                                        <WarningIcon fontSize="small" />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenPaymentDialog(invoice)}
                                                        color="success"
                                                        title="Realizar Pago"
                                                    >
                                                        <PaymentIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            )}

                                            {/* Solo mostrar botón de promesa si está vencida, no pagada y no tiene promesa previa */}
                                            {invoice.status === 'overdue' && !invoice.payment_promise_date && !isInvoicePaid(invoice) && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenPromiseDialog(invoice)}
                                                    color="warning"
                                                    title="Solicitar Prórroga"
                                                >
                                                    <CalendarIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Diálogo de Selección de Método de Pago */}
            <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: 'text.primary' }}>Pagar Recibo #{selectedInvoice?.invoice_number}</DialogTitle>
                <DialogContent>
                    <Box className="py-2" display="flex" flexDirection="column" gap={2}>
                        <Box sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderRadius: '12px', border: '1px solid', borderColor: 'divider', mb: 2 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                                Monto total a pagar:
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                                S/ {parseFloat(selectedInvoice?.amount - (selectedInvoice?.paid_amount || 0)).toFixed(2)}
                            </Typography>
                        </Box>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                            Seleccione su método de pago:
                        </Typography>

                        {paymentMethods
                            .filter(method => method.visible_to_client)
                            .map((method) => (
                                <Button
                                    key={method.id}
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleSelectPaymentMethod(method)}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        p: 2,
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        color: 'text.primary',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: 'rgba(25, 118, 210, 0.04)'
                                        }
                                    }}
                                    startIcon={
                                        method.logo_url ? (
                                            <img src={method.logo_url} alt={method.name} style={{ width: 24, height: 24, objectFit: 'contain' }} />
                                        ) : (
                                            <PaymentIcon />
                                        )
                                    }
                                >
                                    <Box textAlign="left" ml={1}>
                                        <Typography variant="body1" fontWeight="bold" sx={{ color: 'text.primary' }}>
                                            {method.name}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {method.description || (method.is_online ? 'Pago automático instantáneo' : 'Requiere comprobante')}
                                        </Typography>
                                    </Box>
                                </Button>
                            ))
                        }
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleClosePaymentDialog} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de Promesa de Pago */}
            <Dialog open={openPromiseDialog} onClose={handleClosePromiseDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: 'text.primary' }}>Solicitar Prórroga de Servicio</DialogTitle>
                <DialogContent>
                    <Box className="py-2">
                        <Typography variant="body1" className="mb-4" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                            ¿Está seguro de que desea solicitar una prórroga de pago para la <strong>Factura #{selectedInvoice?.invoice_number}</strong>?
                        </Typography>
                        
                        <Box sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderRadius: '12px', border: '1px solid', borderColor: 'divider', mb: 3 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                Detalle de la solicitud:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                • Monto a Pagar: ${parseFloat(selectedInvoice?.amount || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#eab308', mt: 0.5 }}>
                                • Nueva Fecha Límite (7 días): {promiseDate ? format(promiseDate, 'dd/MM/yyyy') : ''}
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            Al confirmar, su servicio se reactivará inmediatamente por un periodo de 7 días para que pueda reportar su pago. Esta opción solo está disponible una vez por recibo.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleClosePromiseDialog} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
                        Cancelar
                    </Button>
                    <Button onClick={handlePromiseSubmit} variant="contained" color="warning" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', backgroundColor: '#eab308', '&:hover': { backgroundColor: '#ca8a04' } }}>
                        Confirmar Prórroga
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo Visor de PDF (Ticket / A4 / SUNAT) */}
            <Dialog open={pdfModal.open} onClose={() => setPdfModal({ open: false, invoice: null, format: 'ticket' })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <ReceiptIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            {pdfModal.invoice?.sunat_status === 'ACEPTADO' && pdfModal.invoice?.sunat_number
                                ? `Comprobante SUNAT ${pdfModal.invoice.sunat_number}`
                                : `Recibo #${pdfModal.invoice?.invoice_number}`}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setPdfModal({ open: false, invoice: null, format: 'ticket' })}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                        <Typography variant="body2" color="textSecondary">
                            Formato de Previsualización:
                        </Typography>
                        <ToggleButtonGroup
                            size="small"
                            value={pdfModal.format}
                            exclusive
                            onChange={(e, newFormat) => {
                                if (newFormat) setPdfModal(prev => ({ ...prev, format: newFormat }));
                            }}
                            color="primary"
                        >
                            <ToggleButton value="ticket">🎟️ Formato Ticket (80mm)</ToggleButton>
                            <ToggleButton value="a4">📄 Formato A4</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {pdfModal.invoice && (
                        <Box sx={{ width: '100%', height: '70vh', bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                            <iframe
                                src={`/api/client/invoices/${pdfModal.invoice.id}/download?format=${pdfModal.format}&inline=true&token=${localStorage.getItem('clientToken')}`}
                                title="Comprobante PDF"
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default ClientInvoices;